import Icons from '../../Icons'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '@/store/store'
import { selectUser } from '@/store/slices/user/selectors'
import { setOpenModalTryPremium } from '@/store/slices/user/actions'
import { SyncManager } from '@/firebase/SyncManager'
import DefaultButton from '@/components/DefaultButton'
import { customAmplitude } from '@/utils/customAmplitude'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { NavbarButtonBaseProps } from '../navbarConfig'
import { buildPremiumPromptProperties, buildSignupAnalyticsContext } from '@/utils/signupPremiumAnalytics'

interface TryPremiumButtonProps extends NavbarButtonBaseProps {
  /** Yellow, crown-less style used by the create page (dashboard) header. */
  orange?: boolean
  /** Hides the crown icon. Used alongside `orange` on the create page header. */
  hideCrown?: boolean
  /** Shows only the crown, keeping the label as its accessible name and tooltip. */
  iconOnly?: boolean
}

function TryPremiumButton({ disabled = false, premiumSource: premiumSourceProp, orange = false, hideCrown = false, iconOnly = false }: TryPremiumButtonProps) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const user = useSelector(selectUser)
  const location = useLocation()
  const isCreatePage = location.pathname.includes('create')
  const premiumSource = premiumSourceProp ?? (isCreatePage ? 'Create Header_CTA' : 'Header_CTA')

  if (user?.userIsPremium) return null

  const isDisabled = disabled || !SyncManager.shared.hasConnection

  const handleClick = () => {
    dispatch(
      setOpenModalTryPremium({
        isOpen: true,
        source: premiumSource,
        opened: true,
      })
    )
    const eventProperties = buildPremiumPromptProperties(
      buildSignupAnalyticsContext({
        source: premiumSource,
        type: 'Standard',
        pathname: location.pathname,
      }),
    )
    customAmplitude('Premium Prompt', eventProperties)

    // @ts-ignore
    window.dataLayer.push({ event: 'premium_prompt', ...eventProperties })
  }

  return (
    <DefaultButton
      type={orange ? 'primary-orange' : 'secondary-black'}
      size="standard"
      paddingHorizontal={iconOnly ? undefined : '16px'}
      icon={iconOnly ? 'iconOnly' : hideCrown ? 'noIcon' : 'iconLeft'}
      iconComponent={hideCrown && !iconOnly ? undefined : <Icons.PremiumIcon disabled={isDisabled} />}
      label={t('Try Premium')}
      ariaLabel={iconOnly ? t('Try Premium') : undefined}
      tooltip={iconOnly ? t('Try Premium') : undefined}
      disabled={isDisabled}
      onClick={handleClick}
    />
  )
}

export default TryPremiumButton
